import { useCallback, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, Stack, Text } from "@mantine/core";
import DataWrapper from "../../../../common/components/DataWrapper/DataWrapper";
import StatusText from "../../../../common/components/DataTable/StatusText";
import PaymentDrawer from "../../../payments/components/PaymentDrawer/PaymentDrawer";
import PaymentToolbar from "../../../payments/components/PaymentToolbar/PaymentToolbar";
import usePaymentStats from "../../../payments/hooks/usePaymentStats";
import {
  PAYMENTS_SWR_CONFIG,
  INITIAL_PAYMENT_FILTERS,
  buildPaymentFilterQuery,
  formatCurrencyAmount,
  withSearchQuery,
  withUserEmailQuery,
  downloadPaymentInvoice,
} from "../../../payments/utils/paymentConstants";

const endpoint = "issuance/user-specific-payments/";

const dash = (value) => (value == null || value === "" ? "—" : value);

const Cell = ({ value, highlight }) => (
  <Text
    size="sm"
    my="9px"
    fw={500}
    c={highlight ? "#092C4C" : "#6C94B4"}
    component="span"
    style={{
      display: "block",
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {dash(value)}
  </Text>
);

const OperatorPayments = () => {
  const outletContext = useOutletContext();
  const operator = outletContext ? outletContext.operator : null;
  const operatorProfile = useSelector((state) => state.operatorProfile);

  const operatorEmail = operator
    ? operator.operator.email
    : operatorProfile?.operator?.email;

  const paymentsEndpoint = operatorEmail
    ? `${endpoint}?user_email=${encodeURIComponent(operatorEmail)}`
    : endpoint;

  const [filters, setFilters] = useState(INITIAL_PAYMENT_FILTERS);
  const [searchQuery, setSearchQuery] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const filterQuery = useMemo(
    () => buildPaymentFilterQuery(filters),
    [filters]
  );

  const additionalFilterQuery = useMemo(
    () => withUserEmailQuery(filterQuery, operatorEmail),
    [filterQuery, operatorEmail]
  );

  const statsQuery = useMemo(
    () => withSearchQuery(additionalFilterQuery, searchQuery),
    [additionalFilterQuery, searchQuery]
  );

  const { stats } = usePaymentStats(statsQuery);

  const handleRowClick = useCallback((row) => {
    setSelectedPayment(row);
    setDrawerOpen(true);
  }, []);

  const updateFilter = useCallback(
    (key, value) => setFilters((prev) => ({ ...prev, [key]: value })),
    []
  );
  const clearFilters = useCallback(() => setFilters(INITIAL_PAYMENT_FILTERS), []);

  const toolbar = PaymentToolbar({
    filters,
    onChange: updateFilter,
    currencies: stats?.all_currencies,
    isFull: false,
    showClear: Boolean(filterQuery),
    onClear: clearFilters,
  });

  const paymentConfig = useMemo(
    () => ({
      toolbarPy: "md",
      selectionCheckbox: true,
      showFilterButton: true,
      doubleClickRow: true,
      onRowClick: handleRowClick,
      highlightOnHover: false,
      getRowStyle: (_, index) => ({
        backgroundColor: index % 2 === 0 ? "#F8F8F8" : "#FFFFFF",
      }),
      fields: [
        {
          key: "transaction_id",
          label: "Transaction Id.",
          sortable: false,
          render: (row) => <Cell value={row.transaction_id} highlight />,
        },
        {
          key: "total_amount",
          label: "Price",
          sortable: false,
          render: (row) => (
            <Cell
              value={formatCurrencyAmount(row.currency, row.total_amount)}
            />
          ),
        },
        {
          key: "method",
          label: "Payment Method",
          sortable: false,
          render: (row) => <Cell value={row.method} />,
        },
        {
          key: "transaction_status",
          label: "Status",
          sortable: false,
          render: (row) => (
            <StatusText status={row.transaction_status} field="status" />
          ),
        },
        {
          key: "view",
          label: "Action",
          sortable: false,
          render: (row) => (
            <Button
              size="compact-sm"
              variant="gradient"
              gradient={{ from: "#0053A8", to: "#033F7C", deg: 270 }}
              onClick={(event) => {
                event.stopPropagation();
                handleRowClick(row);
              }}
            >
              View
            </Button>
          ),
        },
      ],
    }),
    [handleRowClick]
  );

  return (
    <Stack gap="md">
      <DataWrapper
        endpoint={paymentsEndpoint}
        config={paymentConfig}
        additionalFilterQuery={additionalFilterQuery}
        toolbarBottomContent={toolbar.toolbarBottomContent}
        searchPlaceholder="Search transaction ID..."
        onSearchChange={setSearchQuery}
        swrConfig={PAYMENTS_SWR_CONFIG}
      />
      <PaymentDrawer
        opened={drawerOpen}
        payment={selectedPayment}
        onClose={() => setDrawerOpen(false)}
        onDownloadInvoice={downloadPaymentInvoice}
      />
    </Stack>
  );
};

export default OperatorPayments;
